import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
    TrendingUp,
    TrendingDown,
    DollarSign,
    Download,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    Loader2,
    FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function FinancialPage() {
    const { t, language } = useLanguage();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [cashFlow, setCashFlow] = useState(null);
    const [downloading, setDownloading] = useState(false);

    // Default to current month
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchCashFlow();
    }, []);

    const fetchCashFlow = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/financial/cash-flow`, {
                headers,
                params: { startDate, endDate }
            });
            setCashFlow(response.data.data);
        } catch (error) {
            console.error('Failed to fetch cash flow:', error);
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = async () => {
        setDownloading(true);
        try {
            const response = await axios.get(`${API_URL}/financial/report/pdf`, {
                headers,
                params: { startDate, endDate },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `rapport-financier-${startDate}-${endDate}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download PDF:', error);
            toast.error(t('downloadFailed'));
        } finally {
            setDownloading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold">{t('financialDashboard')}</h1>
                        <p className="text-muted-foreground">{t('financialOverview')}</p>
                    </div>
                    <Button onClick={downloadPDF} disabled={downloading || loading}>
                        {downloading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <FileText className="h-4 w-4 mr-2" />
                        )}
                        {t('downloadReport')}
                    </Button>
                </div>

                {/* Date Range Filter */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row items-end gap-4">
                            <div className="grid gap-2">
                                <Label htmlFor="startDate">{t('startDate')}</Label>
                                <Input
                                    id="startDate"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="endDate">{t('endDate')}</Label>
                                <Input
                                    id="endDate"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                            <Button onClick={fetchCashFlow} disabled={loading}>
                                <Calendar className="h-4 w-4 mr-2" />
                                {t('apply')}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                ) : cashFlow && (
                    <>
                        {/* Summary Cards */}
                        {user?.role !== 'TYPE_A' && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <Card className="border-l-4 border-l-green-500">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium text-muted-foreground">{t('income')}</CardTitle>
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-green-600">
                                            {formatCurrency(cashFlow.summary.totalIncome)}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {t('ordersDeliveredCount').replace('{0}', cashFlow.counts.income)}
                                        </p>
                                    </CardContent>
                                </Card>

                                <Card className="border-l-4 border-l-red-500">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium text-muted-foreground">{t('expensesTitle')}</CardTitle>
                                        <TrendingDown className="h-4 w-4 text-red-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-red-600">
                                            {formatCurrency(cashFlow.summary.totalExpenses)}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {t('expensesCount').replace('{0}', cashFlow.counts.expenses)}
                                        </p>
                                    </CardContent>
                                </Card>

                                <Card className="border-l-4 border-l-amber-500">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium text-muted-foreground">{t('pendingExpenses')}</CardTitle>
                                        <DollarSign className="h-4 w-4 text-amber-500" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-amber-600">
                                            {formatCurrency(cashFlow.summary.pendingExpenses)}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t('unpaidExpenses')}</p>
                                    </CardContent>
                                </Card>

                                <Card className={`border-l-4 ${cashFlow.summary.netProfit >= 0 ? 'border-l-green-500' : 'border-l-red-500'}`}>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium text-muted-foreground">{t('netProfit')}</CardTitle>
                                        {cashFlow.summary.netProfit >= 0 ? (
                                            <ArrowUpRight className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <ArrowDownRight className="h-4 w-4 text-red-500" />
                                        )}
                                    </CardHeader>
                                    <CardContent>
                                        <div className={`text-2xl font-bold ${cashFlow.summary.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatCurrency(cashFlow.summary.netProfit)}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t('incomeMinusExpenses')}</p>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Transactions Table */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('transactions')}</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('type')}</TableHead>
                                            <TableHead>{t('expenseDate')}</TableHead>
                                            <TableHead>{t('description')}</TableHead>
                                            <TableHead>{t('expenseCategory')}</TableHead>
                                            <TableHead>{t('reference')}</TableHead>
                                            <TableHead className="text-right">{t('expenseAmount')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {cashFlow.transactions.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                                                    {t('noTransactions')}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            cashFlow.transactions.map((tx) => (
                                                <TableRow key={`${tx.type}-${tx.id}`}>
                                                    <TableCell>
                                                        <Badge
                                                            variant="outline"
                                                            className={tx.type === 'INCOME'
                                                                ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                                                : tx.type === 'RETURN'
                                                                ? 'bg-orange-500/10 text-orange-600 border-orange-500/20'
                                                                : 'bg-red-500/10 text-red-600 border-red-500/20'
                                                            }
                                                        >
                                                            {tx.type === 'INCOME' ? (
                                                                <ArrowUpRight className="h-3 w-3 mr-1" />
                                                            ) : (
                                                                <ArrowDownRight className="h-3 w-3 mr-1" />
                                                            )}
                                                            {tx.type === 'INCOME' ? t('income') : tx.type === 'RETURN' ? 'Retour' : t('expensesTitle')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>{formatDate(tx.date)}</TableCell>
                                                    <TableCell className="font-medium">{tx.description}</TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant="outline"
                                                            style={tx.categoryColor ? {
                                                                borderColor: tx.categoryColor,
                                                                color: tx.categoryColor
                                                            } : {}}
                                                        >
                                                            {tx.category}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {tx.reference || '-'}
                                                    </TableCell>
                                                    <TableCell className={`text-right font-medium ${tx.type === 'INCOME' ? 'text-green-600' : tx.type === 'RETURN' ? 'text-orange-600' : 'text-red-600'}`}>
                                                        {tx.type === 'INCOME' ? '+' : '-'}{formatCurrency(tx.amount)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}
